import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';

import { Schema } from '@typings';
import { formatModelName, getNestedValue } from '@utils';

import { Badge, Button, Field, Icon, Render } from '@components';

export const SchemaTags = ({
  name,
  error,
  label,
  required,
  note = 'Split tags by comma',
}: Schema.CommonNodeProps) => {
  const { register, setValue, formState } = useFormContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    register(name);
  }, [name, register]);

  useEffect(() => {
    setValue(name, tags);
  }, [tags, setValue, name]);

  useEffect(() => {
    const newDefaultValue = getNestedValue(formState.defaultValues, name);

    if (newDefaultValue) {
      setTags(newDefaultValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.defaultValues]);

  const handleTagsAdd = () => {
    const input = inputRef.current!;
    const newTags = input.value.split(',');
    const formattedTags = newTags
      .map(formatModelName)
      .filter((tag) => tag && !tags.includes(tag));

    setTags(tags.concat(formattedTags));
    input.value = '';
  };

  const handleTagRemove = (targetTag: string) => {
    const filteredTags = tags.filter((tag) => tag !== targetTag);

    setTags(filteredTags);
  };

  return (
    <div>
      <div className="flex w-full">
        <Field.Input
          ref={inputRef}
          note={note}
          label={label}
          error={error}
          required={required}
          containerClassName="flex-1"
          className="rounded-r-none"
        />

        <Button className="h-14 rounded-l-none px-8" onClick={handleTagsAdd}>
          Add
        </Button>
      </div>

      <Render if={tags}>
        <div className="mt-4 flex flex-wrap gap-3 gap-y-4">
          {tags.map((tag) => (
            <Badge key={tag} className="gap-1">
              {tag}

              <Button
                theme
                className="text-neural-03 transition-colors hover:text-black"
                onClick={() => handleTagRemove(tag)}
              >
                <Icon icon={faXmark} className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      </Render>
    </div>
  );
};
